// src/products/analysis/pages/CommoditiesPage.js
import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle, TrendingUp, TrendingDown, RefreshCw, Package } from 'lucide-react';
import { getCommodityQuote, getNonEnergyCommodities } from '../services/commodityService';

const CommoditiesPage = () => {
  const [commodities, setCommodities] = useState([]);
  const [error, setError] = useState(null);
  const [loadingCommodities, setLoadingCommodities] = useState({});

  useEffect(() => {
    // Initialize with commodity list (show cards immediately)
    const nonEnergyCommodities = getNonEnergyCommodities();
    setCommodities(nonEnergyCommodities.map(c => ({
      ...c,
      currentPrice: null,
      priceChange: null,
      priceChangePercent: null,
      date: null
    })));
    
    // Then load prices asynchronously
    loadCommodityPrices(nonEnergyCommodities);
  }, []);

  const loadCommodityPrices = async (commodityList) => {
    setError(null);
    
    // Load prices for each commodity with delays to avoid rate limits
    for (let i = 0; i < commodityList.length; i++) {
      const commodity = commodityList[i];
      
      // Small delay between requests to avoid overwhelming Yahoo Finance (no official rate limit, but be respectful)
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay
      }
      
      setLoadingCommodities(prev => ({ ...prev, [commodity.symbol]: true }));
      
      try {
        const data = await getCommodityQuote(commodity.symbol);
        if (data) {
          setCommodities(prev => prev.map(c => 
            c.symbol === commodity.symbol 
              ? { ...c, ...data }
              : c
          ));
        }
      } catch (err) {
        console.warn(`Failed to fetch ${commodity.name}:`, err.message);
        // Keep the commodity card but mark it as failed
        setCommodities(prev => prev.map(c => 
          c.symbol === commodity.symbol 
            ? { ...c, error: err.message }
            : c
        ));
      } finally {
        setLoadingCommodities(prev => ({ ...prev, [commodity.symbol]: false }));
      }
    }
  };

  const loadCommodityData = async () => {
    const nonEnergyCommodities = getNonEnergyCommodities();
    
    // Reset to initial state
    setCommodities(nonEnergyCommodities.map(c => ({
      ...c,
      currentPrice: null,
      priceChange: null,
      priceChangePercent: null,
      date: null,
      error: null
    })));
    
    // Reload prices
    await loadCommodityPrices(nonEnergyCommodities);
  };

  const formatPrice = (price, unit) => {
    if (price === null || price === undefined) return 'N/A';
    
    if (price >= 1000) {
      return `${unit} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (price >= 1) {
      return `${unit} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    } else {
      return `${unit} ${price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
    }
  };

  const formatChange = (change, changePercent) => {
    if (changePercent === null || changePercent === undefined) {
      if (change === null || change === undefined) return 'N/A';
      return `${change >= 0 ? '+' : ''}${change.toFixed(2)}`;
    }
    
    const isPositive = changePercent >= 0;
    const sign = isPositive ? '+' : '';
    return `${sign}${changePercent.toFixed(2)}%`;
  };

  // Group commodities by category
  const groupedCommodities = commodities.reduce((acc, commodity) => {
    const category = commodity.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(commodity);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commodities Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Major commodity prices and trends (energy benchmarks live in the Energy Markets view)
          </p>
        </div>
        <button
          onClick={loadCommodityData}
          disabled={Object.values(loadingCommodities).some(Boolean)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {Object.values(loadingCommodities).some(Boolean) ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh Prices
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5" />
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
            <p className="text-xs mt-1">Yahoo Finance provides free commodity futures data. Loading prices...</p>
          </div>
        </div>
      )}

      {/* Commodity Cards by Category */}
      {commodities.length > 0 && (
        <div className="space-y-8">
          {Object.entries(groupedCommodities).map(([category, categoryCommodities]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {categoryCommodities.map((commodity) => {
                  const isPositive = commodity.priceChangePercent >= 0;
                  const isLoading = loadingCommodities[commodity.symbol];

                  return (
                    <div
                      key={commodity.symbol}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{commodity.name}</h3>
                          <p className="text-sm text-gray-500">{commodity.symbol}</p>
                        </div>
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>

                      <div className="space-y-3">
                        {/* Current Price */}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Current Price</p>
                          {isLoading ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                              <span className="text-sm text-gray-400">Loading...</span>
                            </div>
                          ) : commodity.error ? (
                            <p className="text-sm text-red-500">Error loading price</p>
                          ) : commodity.currentPrice !== null ? (
                            <p className="text-2xl font-bold text-gray-900">
                              {formatPrice(commodity.currentPrice, commodity.unit)}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">Waiting to load...</p>
                          )}
                        </div>

                        {/* Price Change */}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Daily Change</p>
                          <div className="flex items-center gap-2">
                            {isLoading ? (
                              <p className="text-sm text-gray-400">Loading...</p>
                            ) : commodity.error ? (
                              <p className="text-xs text-red-500">{commodity.error}</p>
                            ) : commodity.priceChangePercent !== null && commodity.priceChangePercent !== undefined ? (
                              <>
                                {isPositive ? (
                                  <TrendingUp className="w-5 h-5 text-green-500" />
                                ) : (
                                  <TrendingDown className="w-5 h-5 text-red-500" />
                                )}
                                <p
                                  className={`text-lg font-semibold ${
                                    isPositive ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  {formatChange(commodity.priceChange, commodity.priceChangePercent)}
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">N/A</p>
                            )}
                          </div>
                        </div>

                        {/* Last Updated */}
                        {commodity.date && (
                          <div className="pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500">
                              Last updated: {new Date(commodity.date).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Message */}
      {commodities.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-2 text-blue-800">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">Prices are loading in the background</p>
            <p className="text-xs mt-1">Yahoo Finance provides free commodity futures data. Each commodity will update as data becomes available.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommoditiesPage;

